/**
 * Selection: shared by the viewport, the Outliner and the Inspector
 * (SPEC §4.6). An ordered set of entities plus the active one (the last
 * selected, which the Inspector shows).
 *
 * Changes go through `selection.set`. User selection is undoable, as in Unreal
 * and Unity. Tour-driven selection passes `transient: true` and is not
 * recorded (A-15), so a tour never leaves steps in the undo stack.
 */
import { parsePayload } from "../../commands/payload";
import type { CommandDefinition } from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import type { EntityId } from "../../world/entity";
import type { WorldReader } from "../../world/World";

export interface SelectionState {
  readonly ids: readonly EntityId[];
  readonly active: EntityId | null;
}

export interface SelectionWriter {
  set(state: SelectionState): void;
}

export interface SetSelectionPayload {
  readonly ids: readonly EntityId[];
  readonly active?: EntityId;
  /** Not recorded in undo history (tour-driven selection). */
  readonly transient?: boolean;
}

export const EMPTY_SELECTION: SelectionState = { ids: [], active: null };

export class Selection implements ReadableStore<SelectionState> {
  /** Handed only to selection commands and the level commit. */
  readonly writer: SelectionWriter;
  private readonly store: VersionedStore<SelectionState>;

  constructor(scheduler: ChangeScheduler) {
    this.store = new VersionedStore(scheduler, EMPTY_SELECTION);
    this.writer = {
      set: (state) => {
        this.store.publish(state);
      },
    };
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): SelectionState => this.store.getSnapshot();
}

/** The store is the single source of truth: memos come from it, never from a cached copy. */
export function selectionCommands(selection: Selection, world: WorldReader): CommandDefinition[] {
  const setSelection: CommandDefinition<SetSelectionPayload, SelectionState> = {
    type: "selection.set",
    undoable: (payload) => payload.transient !== true,
    validate: (raw) => {
      const shape = parsePayload(raw, {
        ids: "string[]",
        active: "string?",
        transient: "boolean?",
      });
      if (!shape.ok) return shape;

      const ids = unique(shape.value.ids as readonly EntityId[]);
      const missing = ids.find((id) => !world.get(id));
      if (missing) return { ok: false, error: `No entity '${missing}'` };

      const active = shape.value.active as EntityId | undefined;
      if (active !== undefined && !ids.includes(active))
        return { ok: false, error: `active '${active}' is not in ids` };

      return {
        ok: true,
        value: {
          ids,
          ...(active !== undefined && { active }),
          ...(shape.value.transient && { transient: true }),
        },
      };
    },
    do: (payload) => {
      const previous = selection.getSnapshot();
      selection.writer.set({
        ids: payload.ids,
        active: payload.active ?? payload.ids.at(-1) ?? null,
      });
      return previous;
    },
    undo: (_payload, previous) => {
      selection.writer.set(previous);
    },
    describe: (payload) => describeSelection(payload.ids, world),
  };

  return [setSelection] as CommandDefinition[];
}

/** "Select Zone_A", "Select 2 entities", or "Clear selection". */
function describeSelection(ids: readonly EntityId[], world: WorldReader): string {
  if (ids.length === 0) return "Clear selection";
  const [only] = ids;
  if (ids.length === 1 && only) return `Select ${world.get(only)?.name ?? only}`;
  return `Select ${ids.length} entities`;
}

function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}
